import type { ClientQuestion, LocaleCode } from '@fixit/bozp-content';
import { QuizWidget } from '@fixit/bozp-renderer';
import { useQuery } from '@tanstack/react-query';
import { createRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { type Control, useController, useForm } from 'react-hook-form';
import { getBozpToken } from '@/api/bozp-token';
import { type AnswerSubmission, getTraining, submitCompletion } from '@/api/training';
import { Masthead } from '@/components/Masthead';
import { useT } from '@/i18n';
import { useLocale } from '@/i18n/locale';
import { decodeJwtSub } from '@/lib/jwt';
import { clearProgress } from '@/lib/progress-db';
import { deriveWeakChapterIds } from '@/lib/quiz-result';
import { seededShuffle } from '@/lib/shuffle';
import { createTelemetryQueue } from '@/lib/telemetry';
import { clearTrainingSession, ensureTrainingSession } from '@/lib/training-session';
import { setLastResult } from '@/result-store';
import { markTrainingValid } from '@/session';
import { Route as rootRoute } from './__root';

/**
 * Version of the SES (simple electronic signature) acknowledgment the worker
 * confirms before submitting. Must match a key in the API's
 * BOZP_ACK_DECLARATIONS map — only the version is sent + persisted.
 */
const BOZP_ACK_DECLARATION_VERSION = 'v1';

/** Form values keyed by question id; each value is a `QuizAnswer` (string | string[] | number | boolean). */
type QuizFormValues = Record<string, string | string[] | number | boolean>;

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/training/$hazardId/quiz',
  component: QuizPage,
});

/**
 * Bridges react-hook-form to the CONTROLLED `QuizWidget` ({ question, value, onChange, locale }).
 * `useController` registers the field under `question.id`; the widget renders the current value
 * and reports changes back through `onChange`.
 *
 * `question` is typed `ClientQuestion` to match the renderer's `QuizWidgetProps` and the
 * answer-key-stripped shape the player holds (`correct`/`tolerance` removed server-side).
 * No answer key ever enters the player bundle.
 */
function QuizField({
  question,
  control,
  locale,
}: {
  question: ClientQuestion;
  control: Control<QuizFormValues>;
  locale: LocaleCode;
}) {
  const { field } = useController({ name: question.id, control });
  return (
    <QuizWidget question={question} value={field.value} onChange={field.onChange} locale={locale} />
  );
}

/**
 * Per-attempt shuffle seed. `crypto.randomUUID` exists ONLY in a secure context, so on any
 * plain-HTTP origin — the Devbox mesh address, a LAN IP used for phone testing — it is
 * undefined and calling it crashed the whole quiz route (observed 2026-09-12 on
 * http://10.8.0.10:<port>). `crypto.getRandomValues` carries no such restriction; the last
 * branch is a non-cryptographic fallback, which is fine: this value only shuffles questions.
 */
function randomAttemptId(): string {
  const webCrypto = globalThis.crypto as Crypto | undefined;
  if (typeof webCrypto?.randomUUID === 'function') return webCrypto.randomUUID();
  if (typeof webCrypto?.getRandomValues === 'function') {
    const bytes = webCrypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

function QuizPage() {
  const t = useT();
  const LOCALE = useLocale();
  const navigate = useNavigate();
  const { hazardId } = useParams({ from: '/training/$hazardId/quiz' });
  const userId = useMemo(() => decodeJwtSub(getBozpToken() ?? '') ?? 'anon', []);

  // A fresh seed per training mount = a reshuffled attempt (anti-memorization); folding
  // `hazardId` into the value makes the re-compute-on-hazard-change intent a real dependency.
  const attemptSeed = useMemo(() => `${hazardId}:${randomAttemptId()}`, [hazardId]);
  // The SAME server session the training page recorded the chapters in
  // (sessionStorage); the server refuses a submit whose session did not see
  // every chapter. `startedAt` is display-only — the server measures time on
  // task from the session's own timestamps.
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionError, setSessionError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<'retry' | 'restart' | null>(null);
  const [restarting, setRestarting] = useState(false);
  const [restartError, setRestartError] = useState(false);
  // SES acknowledgment: the worker must confirm the declaration before submit.
  const [acknowledged, setAcknowledged] = useState(false);

  const { data: training } = useQuery({
    queryKey: ['training', hazardId],
    queryFn: () => getTraining(hazardId),
  });

  const questions = useMemo<ClientQuestion[]>(() => {
    if (!training) return [];
    const shuffledQs = seededShuffle(training.questions, attemptSeed);
    // `truefalse`/`numeric` members have no `choices`; only reshuffle the choice-bearing kinds,
    // narrowing per-discriminant so each question stays its proper `ClientQuestion` member.
    return shuffledQs.map((q) =>
      'choices' in q ? { ...q, choices: seededShuffle(q.choices, `${attemptSeed}:${q.id}`) } : q,
    );
  }, [training, attemptSeed]);

  const { handleSubmit, control } = useForm<QuizFormValues>({ defaultValues: {} });

  useEffect(() => {
    if (!training) return;
    let cancelled = false;
    ensureTrainingSession(training)
      .then((s) => {
        if (!cancelled) setSessionId(s.sessionId);
      })
      .catch(() => {
        if (!cancelled) setSessionError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [training]);

  if (!training || (!sessionId && !sessionError)) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-10 text-ink-soft">
        <span aria-hidden className="hb-spinner" />
        <p>{t('Loading…')}</p>
      </div>
    );
  }

  const onSubmit = handleSubmit(async (values) => {
    // Couple the recorded SES layer to the worker's actual confirmation (the submit
    // button is already gated on this, but enforce it at the payload level too).
    if (!acknowledged || !sessionId) return;
    setSubmitting(true);
    setSubmitError(null);
    const answers: AnswerSubmission[] = questions.map((q) => ({
      questionId: q.id,
      choice: values[q.id] ?? null,
    }));
    try {
      const res = await submitCompletion({
        hazardProfileId: training.id,
        sessionId,
        answers,
        acknowledgedDeclarationVersion: acknowledged ? BOZP_ACK_DECLARATION_VERSION : undefined,
      });
      const telemetry = createTelemetryQueue({
        hazardProfileId: training.id,
        sessionId,
        contentVersion: training.versionHash,
        schemaVersion: training.content.schemaVersion,
      });
      void telemetry.emit(res.passed ? 'passed' : 'failed', {
        result: { success: res.passed, completion: true, score: { scaled: res.score / 100 } },
      });
      if (res.passed) {
        markTrainingValid(training.id);
        await clearProgress(userId, training.id);
        clearTrainingSession(training.id);
      }
      setLastResult({
        hazardProfileId: training.id,
        passed: res.passed,
        score: res.score,
        // Server returns wrongQuestionIds (questions graded 0); map them → distinct chapter ids
        // for chapter-level weak-topic feedback (Decision 9).
        weakChapterIds: deriveWeakChapterIds(questions, res.wrongQuestionIds),
      });
      navigate({ to: '/training/$hazardId/result', params: { hazardId } });
    } catch (error) {
      const code =
        error && typeof error === 'object' && 'code' in error && typeof error.code === 'string'
          ? error.code
          : undefined;
      const needsRestart = code === 'bozp.session.incomplete' || code === 'bozp.session.invalid';
      if (!needsRestart) console.error('BOZP completion request failed', { code });
      setSubmitError(needsRestart ? 'restart' : 'retry');
      setSubmitting(false);
    }
  });

  const restartTraining = async () => {
    setRestarting(true);
    setRestartError(false);
    try {
      // The rejected session's local checklist cannot prove completion. Start
      // fresh only after the worker explicitly chooses to repeat the course.
      await clearProgress(userId, training.id);
      clearTrainingSession(training.id);
      await navigate({ to: '/training/$hazardId', params: { hazardId } });
    } catch {
      console.error('BOZP training restart failed');
      setRestartError(true);
    } finally {
      setRestarting(false);
    }
  };

  return (
    <div className="flex flex-1 flex-col">
      <Masthead />
      <main className="mx-auto w-full max-w-2xl px-6 pb-24">
        <header className="hb-rise mb-9">
          <p className="hb-eyebrow hb-eyebrow--accent mb-2">{t('Final test')}</p>
          <h1 className="hb-title text-3xl sm:text-4xl">{training.title}</h1>
        </header>

        <form onSubmit={onSubmit} className="flex flex-col gap-5">
          {questions.map((q, i) => (
            <div
              key={q.id}
              className="hb-q hb-rise rounded-[var(--hb-radius)] border border-line bg-card p-5 sm:p-6"
              style={{ animationDelay: `${i * 60 + 60}ms` }}
            >
              <p className="hb-eyebrow mb-3">
                {t('Question {number} of {total}', { number: i + 1, total: questions.length })}
              </p>
              <QuizField question={q} control={control} locale={LOCALE} />
              {q.kind === 'multi' && (
                <p className="mt-3 text-xs text-ink-faint">{t('Select all correct answers')}</p>
              )}
            </div>
          ))}

          <label
            className="hb-q hb-rise flex items-start gap-3 rounded-[var(--hb-radius)] border border-line bg-card p-5 text-sm text-ink-soft"
            data-testid="quiz-ack-label"
          >
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              data-testid="quiz-ack"
              className="mt-0.5 size-4 shrink-0"
            />
            <span>
              {t(
                'I confirm that I have completed this training in full and understood its content.',
              )}
            </span>
          </label>

          {(submitError || sessionError) && (
            <p role="alert" className="font-medium text-fixit-700">
              {sessionError
                ? t('Return to FixIt to continue')
                : submitError === 'restart'
                  ? t(
                      'Your training session could not be accepted. Restart the training and complete each chapter again.',
                    )
                  : t('Your answers could not be submitted. Try submitting them again.')}
            </p>
          )}

          {submitError === 'restart' && (
            <>
              {restartError && (
                <p role="alert" className="font-medium text-fixit-700">
                  {t('Training could not be restarted. Try again.')}
                </p>
              )}
              <button
                type="button"
                disabled={restarting}
                onClick={() => void restartTraining()}
                className="hb-btn hb-btn--primary"
              >
                {t('Restart training')}
              </button>
            </>
          )}

          <button
            type="submit"
            data-testid="quiz-submit"
            disabled={
              submitting || restarting || submitError === 'restart' || !acknowledged || !sessionId
            }
            className="hb-btn hb-btn--primary self-stretch sm:self-end"
          >
            {submitting && (
              <span aria-hidden className="hb-spinner !h-4 !w-4 !border-white/40 !border-t-white" />
            )}
            {t('Submit answers')}
          </button>
        </form>
      </main>
    </div>
  );
}
